"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnalysisMain = void 0;
const lodash_1 = require("lodash");
const request_result_status_enum_1 = require("../@types/incoming/enum/request-result-status.enum");
const technical_impression_1 = require("./impressions/technical.impression");
const moving_average_impression_1 = require("./impressions/moving-average.impression");
const historical_impression_1 = require("./impressions/historical.impression");
const asset_enum_1 = require("../@types/config/asset.enum");
const fundamentalist_impression_1 = require("./impressions/fundamentalist.impression");
const technical_indication_1 = require("./indications/technical.indication");
const moving_average_indication_1 = require("./indications/moving-average.indication");
const fundamental_indication_1 = require("./indications/fundamental-indication");
const news_indication_1 = require("./indications/news-indication");
const average_price_indication_1 = require("./indications/average-price-indication");
const lucy_final_indication_1 = require("./indications/lucy-final.indication");
const health_impression_1 = require("./impressions/health.impression");
const health_indication_1 = require("./indications/health-indication");
class AnalysisMain {
    constructor(data, config) {
        this.readableResults = {};
        this.inputData = data;
        this.config = config;
        const investorType = this.getInvestorType();
        const assetType = this.getAssetType();
        this.techImpressions = new technical_impression_1.TechnicalImpression(investorType);
        this.mmImpressions = new moving_average_impression_1.MovingAverageImpression(investorType);
        this.historicImpressions = new historical_impression_1.HistoricalImpression(investorType, assetType);
        this.fundamentalImpressions = new fundamentalist_impression_1.FundamentalistImpression();
        this.healthImpressions = new health_impression_1.HealthImpressions();
        this.techIndication = new technical_indication_1.TechnicalIndication();
        this.mmIndication = new moving_average_indication_1.MovingAverageIndication(investorType);
        this.funIndication = new fundamental_indication_1.FundamentalIndication();
        this.newsIndication = new news_indication_1.NewsIndication();
        this.avgPriceIndication = new average_price_indication_1.AveragePriceIndication();
        this.lucyFinalIndication = new lucy_final_indication_1.LucyFinalIndication(investorType);
        this.healthIndication = new health_indication_1.HealthIndication(investorType);
        this.readableResults.impressions = [];
        this.readableResults.indications = {};
        this.readableResults.indications.extra = {};
        const methods = Object.getOwnPropertyNames(Object.getPrototypeOf(this));
        const bindableMethods = methods.filter((name) => name !== 'constructor');
        bindableMethods.forEach((method) => {
            this[method] = this[method].bind(this);
        });
    }
    process() {
        const availableLoads = [
            this.loadAllImpressions,
            this.loadAllIndicators,
        ];
        availableLoads.forEach((load) => load());
    }
    getReadableResult() {
        return !(0, lodash_1.isEmpty)(this.inputData) ? this.readableResults : null;
    }
    loadAllImpressions() {
        const availableLoads = [
            this.loadTechImpressions,
            this.loadHistoricalImpressions,
            this.loadFundamentalImpressions,
            this.loadHealthImpressions,
        ];
        availableLoads.forEach((load) => load());
        this.readableResults.impressions = this.readableResults.impressions.filter((impression) => impression);
    }
    loadAllIndicators() {
        const availableSubLoads = [
            this.loadTechnicalIndicators,
            this.loadFundamentalIndicators,
            this.loadExtraIndicators,
            this.loadLucyGeneralIndicators,
            this.loadHealthIndicators,
        ];
        availableSubLoads.forEach((load) => {
            load();
        });
    }
    loadTechImpressions() {
        var _a, _b;
        const config = this.config;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        const resultCodes = this.inputData.requestsResults;
        if (config.impressions.technical &&
            (resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Technicals) === request_result_status_enum_1.eRequestResultStatus.SUCCESS &&
            ((_b = analysisResults.Technicals) === null || _b === void 0 ? void 0 : _b.TechnicalIndicators)) {
            const techIndicators = analysisResults.Technicals.TechnicalIndicators;
            const mmIndicators = analysisResults.Technicals.MovingAverage;
            this.readableResults.impressions = [
                ...this.readableResults.impressions,
                ...this.techImpressions.getAll(techIndicators),
                ...this.mmImpressions.getAll(mmIndicators),
            ];
        }
    }
    loadHistoricalImpressions() {
        var _a;
        const config = this.config;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        const resultCodes = this.inputData.requestsResults;
        if (config.impressions.historical &&
            (resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Historical) === request_result_status_enum_1.eRequestResultStatus.SUCCESS &&
            (analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Historical)) {
            this.readableResults.impressions = [
                ...this.readableResults.impressions,
                ...this.historicImpressions.getAll(analysisResults.Historical),
            ];
        }
    }
    loadFundamentalImpressions() {
        var _a;
        const config = this.config;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        const resultCodes = this.inputData.requestsResults;
        const assetType = this.getAssetType();
        if (assetType === asset_enum_1.eAssetType.STOCK &&
            config.impressions.fundamental &&
            this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Fundamental) &&
            (analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Fundamental)) {
            this.readableResults.impressions = [
                ...this.readableResults.impressions,
                ...this.fundamentalImpressions.getAll(analysisResults.Fundamental),
            ];
        }
    }
    loadHealthImpressions() {
        var _a;
        const config = this.config;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        const resultCodes = this.inputData.requestsResults;
        const assetType = this.getAssetType();
        if (assetType === asset_enum_1.eAssetType.STOCK &&
            config.impressions.health &&
            this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Health) &&
            (analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Health)) {
            this.readableResults.impressions = [
                ...this.readableResults.impressions,
                ...this.healthImpressions.getAll(analysisResults.Health),
            ];
        }
    }
    loadTechnicalIndicators() {
        var _a, _b, _c;
        const config = this.config;
        const resultCodes = this.inputData.requestsResults;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        if (!config.indications.technical) {
            return;
        }
        if (this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Technicals)) {
            const technicalData = (_b = analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Technicals) === null || _b === void 0 ? void 0 : _b.TechnicalIndicators;
            const movingAverageData = (_c = analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Technicals) === null || _c === void 0 ? void 0 : _c.MovingAverage;
            if (technicalData) {
                this.readableResults.indications.technical =
                    this.techIndication.getIndication(technicalData);
            }
            else {
                this.readableResults.indications.technical = 'error';
            }
            if (movingAverageData) {
                this.readableResults.indications.movingAverage =
                    this.mmIndication.getIndication(movingAverageData);
            }
            else {
                this.readableResults.indications.movingAverage = 'error';
            }
        }
        else if (this.isRequestError(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Technicals)) {
            this.readableResults.indications.technical = 'error';
            this.readableResults.indications.movingAverage = 'error';
        }
    }
    loadFundamentalIndicators() {
        var _a;
        const config = this.config;
        const resultCodes = this.inputData.requestsResults;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        if (!config.indications.fundamental) {
            return;
        }
        if (this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Fundamental)) {
            const fundamentalData = analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Fundamental;
            if (fundamentalData) {
                this.readableResults.indications.fundamental =
                    this.funIndication.getIndication(fundamentalData);
            }
        }
        else if (this.isRequestError(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Fundamental)) {
            this.readableResults.indications.fundamental = 'empty';
        }
        else {
            this.readableResults.indications.fundamental = 'error';
        }
    }
    loadHealthIndicators() {
        var _a;
        const config = this.config;
        const resultCodes = this.inputData.requestsResults;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        if (!config.indications.health) {
            return;
        }
        if (this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Health)) {
            const healthData = analysisResults === null || analysisResults === void 0 ? void 0 : analysisResults.Health;
            if (healthData) {
                this.readableResults.indications.health =
                    this.healthIndication.getIndication(healthData);
            }
        }
        else if (this.isRequestError(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Health)) {
            this.readableResults.indications.health = 'empty';
        }
        else {
            this.readableResults.indications.health = 'error';
        }
    }
    loadExtraIndicators() {
        var _a;
        const config = this.config;
        const resultCodes = this.inputData.requestsResults;
        const analysisResults = (_a = this.inputData) === null || _a === void 0 ? void 0 : _a.analysisResults;
        if (!config.indications.extra) {
            return;
        }
        else if (this.isRequestError(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.News) &&
            this.isRequestError(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Historical)) {
            this.readableResults.indications.extra = 'empty';
            return;
        }
        else if (this.readableResults.indications.extra === 'empty') {
            return;
        }
        if (this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.News)) {
            const newsData = analysisResults.News;
            if (newsData) {
                this.readableResults.indications.extra.news =
                    this.newsIndication.getIndication(newsData);
            }
        }
        if (this.isRequestSuccess(resultCodes === null || resultCodes === void 0 ? void 0 : resultCodes.Historical)) {
            const historicalData = analysisResults.Historical;
            if (historicalData) {
                this.readableResults.indications.extra.averagePrice =
                    this.avgPriceIndication.getIndication(historicalData);
            }
        }
    }
    loadLucyGeneralIndicators() {
        this.readableResults.indications.lucyGeneral =
            this.lucyFinalIndication.getIndication(this.readableResults.indications);
    }
    getInvestorType() {
        return this.inputData.analysisResults.Informations.investorType;
    }
    getAssetType() {
        return this.config.asset.type;
    }
    isRequestSuccess(requestResult) {
        return requestResult === request_result_status_enum_1.eRequestResultStatus.SUCCESS;
    }
    isRequestError(requestResult) {
        return requestResult === request_result_status_enum_1.eRequestResultStatus.ERROR;
    }
}
exports.AnalysisMain = AnalysisMain;
//# sourceMappingURL=analysis.main.js.map